// Converts feed-sourced HTML into readable plain text for provider-facing cards.
//
// WHY THIS EXISTS
// External listings are ingested from ~30 scraped job sources and many of them
// carry raw HTML in `description`. The listing card renders that string as
// text, so a provider was shown literal markup -- tags, `&amp;` and `&#39;`
// included -- instead of the prose the recruiter wrote. This module turns the
// markup back into the sentences.
//
// WHY IT NEVER RETURNS HTML
// Those descriptions are untrusted third-party input. What comes out of here
// is TEXT, and callers must render it as text (plain JSX interpolation).
// Never hand it to dangerouslySetInnerHTML: this module is a text extractor,
// not a sanitiser, and treating its output as markup would turn ~30 scraped
// sources into a stored-XSS surface on a provider-facing card.
//
// Every pass is a string pass. Nothing is ever assigned to innerHTML on a live
// document node, so no `<img onerror>` in the input can fire and no `<script>`
// can load while we are reading it -- and the module behaves identically under
// jsdom, node and the browser.
//
// ORDER MATTERS: tags are stripped BEFORE entities are decoded, and entities
// are decoded EXACTLY ONCE. Decoding first (or decoding twice) would turn
// `&amp;lt;` back into a real `<` and re-introduce markup that the strip pass
// had already cleared. See decodeEntitiesOnce below.

// Bullet used to make <li> items readable instead of a run-on sentence.
const BULLET = '• ';

// Elements whose *content* is machine instructions, not prose. Both the tags
// and everything between them are dropped -- surfacing the body of a <script>
// as visible text would be worse than showing the tag.
const DANGEROUS_TAGS = 'applet|embed|head|iframe|noscript|object|script|style|template';

// Elements that imply a line or paragraph boundary. Their boundaries become
// newlines so `<p>a</p><p>b</p>` never collapses into "ab".
const BLOCK_TAGS = [
  'address|article|aside|blockquote|caption|dd|details|dialog|div|dl|dt',
  'fieldset|figcaption|figure|footer|form|h1|h2|h3|h4|h5|h6|header|legend',
  'main|nav|ol|optgroup|option|p|pre|section|summary|table|tbody|tfoot',
  'thead|tr|ul',
].join('|');

// Inline elements carry no boundary at all: they collapse to nothing, so
// `a <strong>$20,000</strong> bonus` keeps its single spaces. br/hr/li/td/th
// appear here only so KNOWN_TAGS is a complete list of element names -- they
// are consumed earlier, as boundaries.
const INLINE_TAGS = [
  'a|abbr|b|bdi|bdo|big|br|button|cite|code|data|del|dfn|em|font|hr|i|img',
  'input|ins|kbd|label|li|mark|meter|nobr|noscript|output|picture|progress',
  'q|rp|rt|ruby|s|samp|select|small|source|span|strike|strong|sub|sup|td',
  'textarea|th|time|track|tt|u|var|video|wbr',
].join('|');

const KNOWN_TAGS = [BLOCK_TAGS, DANGEROUS_TAGS, INLINE_TAGS].join('|');

// Any element name at all, including the `<o:p>` / `<w:sdt>` junk that Word
// paste leaves in a feed description.
const GENERIC_TAG_NAME = '[a-zA-Z][^\\s/>]*';

// An attribute run. Quoted values are consumed whole so a `>` inside
// href="a>b" does not end the tag early and leak `b">` into the prose.
const ATTRS = `(?:"[^"]*"|'[^']*'|[^>"'])*`;

const COMMENT_RE = /<!--[\s\S]*?-->/g;
const CDATA_RE = /<!\[CDATA\[[\s\S]*?\]\]>/g;
const PROCESSING_INSTRUCTION_RE = /<\?[\s\S]*?\?>/g;
const DECLARATION_RE = /<![^>]*>/g;

const DANGEROUS_ELEMENT_RE = new RegExp(`<(${DANGEROUS_TAGS})\\b${ATTRS}>[\\s\\S]*?<\\/\\1\\s*>`, 'gi');
// An UNCLOSED <script> would otherwise survive the tag strip and dump its body
// on the card as text, so everything from the opening tag onward is dropped.
const DANGEROUS_TAIL_RE = new RegExp(`<(?:${DANGEROUS_TAGS})\\b${ATTRS}>[\\s\\S]*$`, 'i');
const DANGEROUS_CLOSE_RE = new RegExp(`<\\/(?:${DANGEROUS_TAGS})\\s*>`, 'gi');

const LINE_BREAK_RE = new RegExp(`<(?:br|hr)\\b${ATTRS}>`, 'gi');
const LIST_ITEM_OPEN_RE = new RegExp(`<li\\b${ATTRS}>`, 'gi');
const LIST_ITEM_CLOSE_RE = /<\/li\s*>/gi;
const CELL_RE = new RegExp(`<\\/?(?:td|th)\\b${ATTRS}>`, 'gi');
// Opening and closing block tags are handled separately so an unclosed
// `<p>a<p>b` breaks into paragraphs the same way `<p>a</p><p>b</p>` does.
const BLOCK_OPEN_RE = new RegExp(`<(?:${BLOCK_TAGS})\\b${ATTRS}>`, 'gi');
const BLOCK_CLOSE_RE = new RegExp(`<\\/(?:${BLOCK_TAGS})\\s*>`, 'gi');

const GENERIC_TAG_RE = new RegExp(`<\\/?${GENERIC_TAG_NAME}${ATTRS}>`, 'g');
const GENERIC_TRUNCATED_RE = new RegExp(`<\\/?${GENERIC_TAG_NAME}[^>]*$`, '');
const KNOWN_TRUNCATED_RE = new RegExp(`<\\/?(?:${KNOWN_TAGS})\\b[^>]*$`, 'i');

// Work is only worth doing when the string actually contains something we would
// change: a tag, an entity, or a typographic dash. A hand-written internal
// listing hits none of these and is handed straight back untouched.
const NEEDS_CONVERSION_RE = /<[a-zA-Z!/?]|&(?:#[0-9]+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,31});|[\u2012-\u2015]/;

// Markup specifically, as opposed to NEEDS_CONVERSION_RE which also fires on
// entities and dashes. Only a tag makes a source newline insignificant.
const HAS_TAG_RE = /<[a-zA-Z!/?]/;

const ENTITY_RE = /&(#[0-9]+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,31});/g;

// Named entities seen in real feed descriptions. `nbsp` decodes to an ordinary
// space so the whitespace pass can collapse it; the zero-width and soft-hyphen
// entities decode to nothing because they are invisible either way.
const NAMED_ENTITIES = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ensp: ' ',
  emsp: ' ',
  thinsp: ' ',
  shy: '',
  zwnj: '',
  zwj: '',
  lsquo: '‘',
  rsquo: '’',
  sbquo: '‚',
  ldquo: '“',
  rdquo: '”',
  bdquo: '„',
  mdash: '—',
  ndash: '–',
  minus: '-',
  hellip: '…',
  bull: '•',
  middot: '·',
  deg: '°',
  plusmn: '±',
  frac12: '½',
  frac14: '¼',
  frac34: '¾',
  times: '×',
  divide: '÷',
  trade: '™',
  reg: '®',
  copy: '©',
  laquo: '«',
  raquo: '»',
  dagger: '†',
  prime: '′',
  Prime: '″',
  euro: '€',
  pound: '£',
  cent: '¢',
  yen: '¥',
  sect: '§',
  para: '¶',
  eacute: 'é',
  egrave: 'è',
  agrave: 'à',
  ccedil: 'ç',
  ntilde: 'ñ',
  uuml: 'ü',
  ouml: 'ö',
  auml: 'ä',
  szlig: 'ß',
  rarr: '→',
  larr: '←',
  harr: '↔',
};

// Legacy feeds emit `&#146;` and friends, which are Windows-1252 bytes rather
// than code points. Decoded literally they become invisible C1 controls, so the
// apostrophes and dashes they really mean are substituted here.
const CP1252 = {
  128: '€',
  130: '‚',
  132: '„',
  133: '…',
  134: '†',
  137: '‰',
  139: '‹',
  145: '‘',
  146: '’',
  147: '“',
  148: '”',
  149: '•',
  150: '–',
  151: '—',
  153: '™',
  155: '›',
};

// Entities legacy feeds shout in upper case. Accented names are excluded on
// purpose -- `&Eacute;` is a different letter from `&eacute;`.
const CASE_INSENSITIVE_ENTITIES = ['amp', 'lt', 'gt', 'quot', 'apos', 'nbsp', 'copy', 'reg'];

function codePointToText(codePoint) {
  if (!Number.isFinite(codePoint) || codePoint <= 0 || codePoint > 0x10ffff) return '';
  if (Object.prototype.hasOwnProperty.call(CP1252, codePoint)) return CP1252[codePoint];
  // Lone surrogates are not text.
  if (codePoint >= 0xd800 && codePoint <= 0xdfff) return '';
  // Control characters become spaces rather than invisible garbage; tab and
  // newline are kept because the whitespace pass understands them.
  if (codePoint === 9 || codePoint === 10) return String.fromCodePoint(codePoint);
  if (codePoint < 32 || (codePoint >= 127 && codePoint <= 159)) return ' ';
  try {
    return String.fromCodePoint(codePoint);
  } catch (err) {
    return '';
  }
}

/**
 * Decodes HTML entities in a single left-to-right pass.
 *
 * Exactly one pass is the security-relevant part. String.replace does not
 * re-scan what it just substituted, so `&amp;lt;` yields the literal four
 * characters `&lt;` and stops there. A second pass would turn that into a real
 * `<` and hand markup back to the caller, which is the classic double-decode
 * bypass of a tag stripper.
 *
 * @param {string} text
 * @returns {string}
 */
function decodeEntitiesOnce(text) {
  if (text.indexOf('&') === -1) return text;
  return text.replace(ENTITY_RE, (match, body) => {
    if (body.charAt(0) === '#') {
      const isHex = body.charAt(1) === 'x' || body.charAt(1) === 'X';
      const codePoint = isHex ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return codePointToText(codePoint);
    }
    if (Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, body)) return NAMED_ENTITIES[body];
    const lower = body.toLowerCase();
    if (CASE_INSENSITIVE_ENTITIES.indexOf(lower) !== -1) return NAMED_ENTITIES[lower];
    // Not an entity we know. Leave it exactly as written rather than guessing.
    return match;
  });
}

/**
 * Removes markup, replacing structural boundaries with newlines so words on
 * either side of a block element never end up glued together.
 *
 * @param {string} text
 * @param {RegExp} tagRe matcher for the remaining tags to erase
 * @param {RegExp} truncatedRe matcher for a tag left unterminated at the end
 * @returns {string}
 */
function stripMarkup(text, tagRe, truncatedRe) {
  let out = text;
  out = out.replace(COMMENT_RE, ' ');
  out = out.replace(CDATA_RE, ' ');
  out = out.replace(PROCESSING_INSTRUCTION_RE, ' ');
  out = out.replace(DECLARATION_RE, ' ');
  out = out.replace(DANGEROUS_ELEMENT_RE, ' ');
  out = out.replace(DANGEROUS_TAIL_RE, ' ');
  out = out.replace(DANGEROUS_CLOSE_RE, ' ');
  out = out.replace(LINE_BREAK_RE, '\n');
  // Opening <li> carries the bullet, so `<li>a<li>b` works without close tags.
  out = out.replace(LIST_ITEM_OPEN_RE, '\n' + BULLET);
  out = out.replace(LIST_ITEM_CLOSE_RE, '\n');
  out = out.replace(CELL_RE, ' ');
  out = out.replace(BLOCK_OPEN_RE, '\n\n');
  out = out.replace(BLOCK_CLOSE_RE, '\n');
  out = out.replace(tagRe, '');
  out = out.replace(truncatedRe, '');
  return out;
}

// Typographic dashes are normalised to hyphens. Feed descriptions are
// user-visible copy on the card and this repo does not ship em dashes; an em
// dash also reads as a clause break, so it becomes a spaced hyphen while an en
// dash (which is nearly always a range, "180-220") does not gain spaces.
function normalizeDashes(text) {
  return text.replace(/[\u2014\u2015]/g, ' - ').replace(/[\u2012\u2013]/g, '-');
}

function normalizeWhitespace(text) {
  return (
    text
      .replace(/\r\n?/g, '\n')
      // Horizontal runs -- spaces, tabs, and any no-break space that survived
      // as a raw character -- become one space. \n is excluded so paragraph
      // structure survives this step.
      .replace(/[^\S\n]+/g, ' ')
      .replace(/ *\n */g, '\n')
      // An <li> with no content would otherwise leave a naked bullet.
      .replace(/^•$/gm, '')
      // Any run of blank lines is one paragraph break, never a gap.
      .replace(/\n{3,}/g, '\n\n')
      // List items sit on consecutive lines rather than being paragraphs apart.
      .replace(/\n{2,}(?=• )/g, '\n')
      // A source that wrote its own marker inside <li> would otherwise be
      // double marked. The trailing space is required so a leading negative
      // number ("-5 percent") is left alone.
      .replace(/^• *([•·‣▪*]|-) +/gm, '• ')
      .trim()
  );
}

/**
 * Turns a string that may or may not contain HTML into readable plain text.
 *
 * The return value is always TEXT and must be rendered as text. Do not pass it
 * to dangerouslySetInnerHTML.
 *
 * @param {*} value description as stored on the listing; anything non-string
 *   (null, undefined, a number, an object) yields ''
 * @returns {string} plain text, entities decoded once, whitespace collapsed and
 *   paragraph breaks preserved
 */
export function htmlToPlainText(value) {
  if (typeof value !== 'string' || value === '') return '';

  if (!NEEDS_CONVERSION_RE.test(value)) {
    // The common case: a hand-written internal listing. Handed back untouched,
    // with no parse paid for -- except that a blank string is reported as
    // empty, so callers can test the result for truthiness.
    return value.trim() === '' ? '' : value;
  }

  // 1. Inside markup a newline is just whitespace, and feed descriptions are
  //    full of source-wrapped lines. Flattening them first means the only
  //    newlines left are the ones the strip pass inserts for real block
  //    boundaries, so a sentence never breaks mid-clause on the card.
  //    This applies ONLY when the string actually carries tags. Text can reach
  //    here for an entity or a dash alone, and in text a newline is the only
  //    paragraph break there is -- flattening it would produce exactly the
  //    run-on block this function exists to prevent.
  let out = HAS_TAG_RE.test(value) ? value.replace(/\r\n?|\n/g, ' ') : value;

  // 2. Strip the markup that is actually present, aggressively: any element
  //    name at all, because scraped feeds carry non-HTML namespaces too.
  out = stripMarkup(out, GENERIC_TAG_RE, GENERIC_TRUNCATED_RE);

  // 3. Decode entities. Once. See decodeEntitiesOnce.
  out = decodeEntitiesOnce(out);

  // 4. Decoding can reveal markup that was double-escaped upstream -- a feed
  //    that published `&lt;p&gt;Nights&lt;/p&gt;` meant a paragraph, not those
  //    characters. Clear it so no tag can leave this function, but match only
  //    real HTML element names this time: after decoding, a bare `<` is far
  //    more likely to be prose ("rates < $200/hr") than a tag, and prose must
  //    survive. `&amp;lt;` decoded to the literal text `&lt;`, which is not
  //    tag-shaped, so it is untouched here -- exactly as intended.
  //
  //    A COMPLETE tag is matched generically here, by the same any-element-name
  //    matcher pass 2 uses. An allowlist of HTML names was not enough: a feed
  //    that escaped `&lt;svg onload=...&gt;`, `&lt;math ...&gt;` or Word's
  //    `&lt;o:p&gt;` namespace junk decoded to a tag whose name is not in the
  //    list, so it survived and printed raw markup on the card -- the very
  //    Defect this module exists to fix. Requiring the `>` is what protects
  //    prose: `Rates &lt; $200/hr` decodes to `< ` (space after the bracket),
  //    which is not tag-shaped and is left alone.
  //
  //    The TRUNCATED matcher stays conservative on purpose. It deletes from the
  //    match to the end of the string, so letting it fire on any unknown word
  //    after a `<` would let decoded prose ending in `<foo` swallow the tail.
  out = stripMarkup(out, GENERIC_TAG_RE, KNOWN_TRUNCATED_RE);

  out = normalizeDashes(out);
  return normalizeWhitespace(out);
}

/**
 * Single-line version of htmlToPlainText, for surfaces too tight to show
 * paragraphs: card fronts, list rows, search results, link subtitles.
 *
 * The markup is removed BEFORE the length is measured. Slicing the raw string
 * instead would spend the budget on tag characters and could cut a tag in
 * half, so a caller that needs a short description must come through here
 * rather than truncate the stored value itself. The cut lands on a word
 * boundary and trailing punctuation is dropped so the ellipsis reads cleanly.
 *
 * The return value is TEXT. Do not pass it to dangerouslySetInnerHTML.
 *
 * @param {*} value description as stored on the listing
 * @param {number} [maxLength=140] characters allowed before the ellipsis
 * @returns {string} one line of plain text, or ''
 */
export function htmlToSnippet(value, maxLength = 140) {
  const text = htmlToPlainText(value).replace(/\n+/g, ' ').replace(/ {2,}/g, ' ').trim();
  if (!text || text.length <= maxLength) return text;

  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
  return `${base.replace(/[\s.,;:!?•-]+$/, '')}...`;
}

export default htmlToPlainText;
